import type { GeoCoordinates } from './types';

/**
 * Best-effort browser geolocation capture for safety-relevant audit events (isolation point
 * verification, override co-authorization, permit issuance) — corroborating "was the actor
 * physically at/near the work site" metadata the research's audit-trail sections call for.
 *
 * Deliberately silent-fail: no geolocation API, no permission granted, a timeout, or any browser
 * error must NEVER block the underlying safety action — this returns `null` in every failure case
 * rather than throwing, and callers always proceed with the action regardless of the result.
 */
export function captureGeolocation(timeoutMs = 4000): Promise<GeoCoordinates | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracyM: pos.coords.accuracy ?? undefined });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}
